/**
 * COMPANY ACCOUNT SERVICE
 *
 * Comprehensive service for managing corporate entity accounts.
 * Handles company registration, officer management, document handling,
 * and KYC workflows for corporate clients.
 *
 * Features:
 * - Company account CRUD operations
 * - Officer management (directors, shareholders, UBOs)
 * - Document upload and verification
 * - KYC status management
 * - Audit trail logging
 */

import type {
 CompanyAccount,
 CompanyOfficer,
 CompanyOfficerRole,
 CompanyDocument,
 CreateCompanyAccountInput,
 UpdateCompanyAccountInput,
 AddCompanyOfficerInput,
 CompanyAccountFull,
 CompanyKYCStatus
} from '../../types/company';

import { getSessionHeaders } from '../serverSession';

// =============================================================================
// API HELPER
// =============================================================================

/**
 * Helper function to call the company API endpoint
 */
async function callCompanyAPI(operation: string, userId: number | null, data: Record<string, any> = {}): Promise<any> {
 const response = await fetch('/api/user/company', {
 method: 'POST',
 headers: { 'Content-Type': 'application/json', ...getSessionHeaders() },
 body: JSON.stringify({ operation, userId, data })
 });

 const result = await response.json();

 if (!response.ok) {
 throw new Error(result.error || `API error: ${response.status}`);
 }

 return result;
}

// =============================================================================
// DATABASE FIELD MAPPERS
// =============================================================================

/**
 * Maps database snake_case fields to TypeScript camelCase
 */
function mapCompanyFromDB(dbRecord: any): CompanyAccount {
 return {
 id: dbRecord.id,
 primaryDirectorUserId: dbRecord.primary_director_user_id,
 legalName: dbRecord.legal_name,
 tradingName: dbRecord.trading_name,
 registrationNumber: dbRecord.registration_number,
 taxIdentificationNumber: dbRecord.tax_identification_number,
 jurisdictionCountryCode: dbRecord.jurisdiction_country_code,
 companyType: dbRecord.company_type,
 dateOfIncorporation: dbRecord.date_of_incorporation,
 industryClassification: dbRecord.industry_classification,
 industryName: dbRecord.industry_name,
 businessDescription: dbRecord.business_description,
 authorizedShareCapital: dbRecord.authorized_share_capital,
 issuedShareCapital: dbRecord.issued_share_capital,
 registeredAddress: {
 street: dbRecord.registered_address_street,
 city: dbRecord.registered_address_city,
 province: dbRecord.registered_address_province,
 postalCode: dbRecord.registered_address_postal_code,
 countryCode: dbRecord.registered_address_country_code
 },
 businessAddress: dbRecord.business_address_street ? {
 street: dbRecord.business_address_street,
 city: dbRecord.business_address_city,
 province: dbRecord.business_address_province,
 postalCode: dbRecord.business_address_postal_code,
 countryCode: dbRecord.business_address_country_code
 } : undefined,
 primaryEmail: dbRecord.primary_email,
 primaryPhone: dbRecord.primary_phone,
 websiteUrl: dbRecord.website_url,
 logoUrl: dbRecord.logo_url,
 kycStatus: dbRecord.kyc_status,
 kycSubmittedAt: dbRecord.kyc_submitted_at,
 kycReviewedAt: dbRecord.kyc_reviewed_at,
 kycReviewedBy: dbRecord.kyc_reviewed_by,
 kycRejectionReason: dbRecord.kyc_rejection_reason,
 kycExpiryDate: dbRecord.kyc_expiry_date,
 isPepAssociated: dbRecord.is_pep_associated,
 isSanctionsChecked: dbRecord.is_sanctions_checked,
 sanctionsCheckDate: dbRecord.sanctions_check_date,
 riskLevel: dbRecord.risk_level,
 isActive: dbRecord.is_active,
 isFrozen: dbRecord.is_frozen,
 freezeReason: dbRecord.freeze_reason,
 metadata: dbRecord.metadata,
 createdAt: dbRecord.created_at,
 updatedAt: dbRecord.updated_at,
 createdBy: dbRecord.created_by
 };
}

/**
 * Maps TypeScript input to database snake_case fields
 */
function __mapCompanyToDB(input: CreateCompanyAccountInput, userId: number): any {
 return {
 primary_director_user_id: userId,
 legal_name: input.legalName,
 trading_name: input.tradingName,
 registration_number: input.registrationNumber,
 tax_identification_number: input.taxIdentificationNumber,
 jurisdiction_country_code: input.jurisdictionCountryCode,
 company_type: input.companyType,
 date_of_incorporation: input.dateOfIncorporation,
 industry_classification: input.industryClassification,
 industry_name: input.industryName,
 business_description: input.businessDescription,
 authorized_share_capital: input.authorizedShareCapital,
 issued_share_capital: input.issuedShareCapital,
 registered_address_street: input.registeredAddress.street,
 registered_address_city: input.registeredAddress.city,
 registered_address_province: input.registeredAddress.province,
 registered_address_postal_code: input.registeredAddress.postalCode,
 registered_address_country_code: input.registeredAddress.countryCode,
 business_address_street: input.businessAddress?.street,
 business_address_city: input.businessAddress?.city,
 business_address_province: input.businessAddress?.province,
 business_address_postal_code: input.businessAddress?.postalCode,
 business_address_country_code: input.businessAddress?.countryCode,
 primary_email: input.primaryEmail,
 primary_phone: input.primaryPhone,
 website_url: input.websiteUrl,
 created_by: userId
 };
}

/**
 * Maps officer from database to TypeScript
 */
function mapOfficerFromDB(dbRecord: any): CompanyOfficer {
 return {
 id: dbRecord.id,
 companyId: dbRecord.company_id,
 userId: dbRecord.user_id,
 role: dbRecord.role,
 customRoleTitle: dbRecord.custom_role_title,
 roleDescription: dbRecord.role_description,
 appointmentDate: dbRecord.appointment_date,
 resignationDate: dbRecord.resignation_date,
 isCurrent: dbRecord.is_current,
 businessEmail: dbRecord.business_email,
 businessPhone: dbRecord.business_phone,
 residentialAddress: dbRecord.residential_address_street ? {
 street: dbRecord.residential_address_street,
 city: dbRecord.residential_address_city,
 postalCode: dbRecord.residential_address_postal_code,
 countryCode: dbRecord.residential_address_country_code
 } : undefined,
 shareholdingPercentage: dbRecord.shareholding_percentage,
 beneficialOwnershipPercentage: dbRecord.beneficial_ownership_percentage,
 isUltimateBeneficialOwner: dbRecord.is_ultimate_beneficial_owner,
 nationality: dbRecord.nationality,
 taxResidencyCountryCode: dbRecord.tax_residency_country_code,
 professionalQualifications: dbRecord.professional_qualifications,
 kycVerified: dbRecord.kyc_verified,
 verificationDate: dbRecord.verification_date,
 isPep: dbRecord.is_pep,
 pepDetails: dbRecord.pep_details,
 sanctionsChecked: dbRecord.sanctions_checked,
 sanctionsCheckDate: dbRecord.sanctions_check_date,
 hasSigningAuthority: dbRecord.has_signing_authority,
 signingAuthorityLimit: dbRecord.signing_authority_limit,
 metadata: dbRecord.metadata,
 createdAt: dbRecord.created_at,
 updatedAt: dbRecord.updated_at
 };
}

/**
 * Maps document from database to TypeScript
 */
function mapDocumentFromDB(dbRecord: any): CompanyDocument {
 return {
 id: dbRecord.id,
 companyId: dbRecord.company_id,
 uploadedByUserId: dbRecord.uploaded_by_user_id,
 documentType: dbRecord.document_type,
 documentName: dbRecord.document_name,
 description: dbRecord.description,
 fileName: dbRecord.file_name,
 filePath: dbRecord.file_path,
 storageBucket: dbRecord.storage_bucket,
 fileSizeBytes: dbRecord.file_size_bytes,
 mimeType: dbRecord.mime_type,
 fileHash: dbRecord.file_hash,
 issueDate: dbRecord.issue_date,
 expiryDate: dbRecord.expiry_date,
 isExpired: dbRecord.is_expired,
 verificationStatus: dbRecord.verification_status,
 qualityScore: dbRecord.quality_score,
 reviewedBy: dbRecord.reviewed_by,
 reviewedAt: dbRecord.reviewed_at,
 rejectionReason: dbRecord.rejection_reason,
 adminNotes: dbRecord.admin_notes,
 metadata: dbRecord.metadata,
 uploadedAt: dbRecord.uploaded_at,
 createdAt: dbRecord.created_at,
 updatedAt: dbRecord.updated_at
 };
}

// =============================================================================
// COMPANY ACCOUNT SERVICE CLASS
// =============================================================================

export class CompanyAccountService {

 // ===========================================================================
 // COMPANY ACCOUNT OPERATIONS
 // ===========================================================================

 /**
 * Create a new company account
 */
 static async createCompanyAccount(
 userId: number,
 input: CreateCompanyAccountInput
 ): Promise<{ success: boolean; data?: CompanyAccount; error?: string }> {
 try {
 const result = await callCompanyAPI('create_company_account', userId, { input });
 return { success: true, data: mapCompanyFromDB(result.data) };
 } catch (error: any) {
 console.error('Exception creating company account:', error);
 return { success: false, error: error.message || 'An unexpected error occurred' };
 }
 }

 /**
 * Get company account by ID
 */
 static async getCompanyById(
 companyId: string
 ): Promise<{ success: boolean; data?: CompanyAccount; error?: string }> {
 try {
 const result = await callCompanyAPI('get_company_by_id', null, { companyId });
 return { success: true, data: mapCompanyFromDB(result.data) };
 } catch (error: any) {
 console.error('Exception fetching company:', error);
 return { success: false, error: error.message || 'An unexpected error occurred' };
 }
 }

 /**
 * Get all companies for a user (as primary director or officer)
 */
 static async getUserCompanies(
 userId: number
 ): Promise<{ success: boolean; data?: CompanyAccount[]; error?: string }> {
 try {
 const result = await callCompanyAPI('get_user_companies', userId, {});
 return { success: true, data: (result.data || []).map(mapCompanyFromDB) };
 } catch (error: any) {
 console.error('Exception fetching user companies:', error);
 return { success: false, error: error.message || 'An unexpected error occurred' };
 }
 }

 /**
 * Get the shares/history attributed to a SINGLE company (Phase 5a).
 *
 * READ-ONLY. Returns rows from aureus_share_purchases (active company holdings)
 * and crypto_payment_transactions (company payment history) that carry
 * purchaser_type='COMPANY' + company_id=companyId. The backend takes the acting
 * user from the session and authorizes only the company's primary director, so a
 * non-director receives an error and NO rows.
 */
 static async getCompanyHoldings(
 companyId: string,
 userId: number
 ): Promise<{
 success: boolean;
 data?: { companyId: string; companyLegalName: string; purchases: any[]; cryptoPayments: any[] };
 error?: string;
 }> {
 try {
 const result = await callCompanyAPI('get_company_holdings', userId, { companyId });
 return { success: true, data: result.data };
 } catch (error: any) {
 console.error('Exception fetching company holdings:', error);
 return { success: false, error: error.message || 'An unexpected error occurred' };
 }
 }

 /**
 * Get the primary company for a user (where they are the primary director)
 * Used for certificate generation to determine if company details should be used
 */
 static async getCompanyByPrimaryDirector(
 userId: number
 ): Promise<{ success: boolean; data?: CompanyAccount | null; error?: string }> {
 try {
 const result = await callCompanyAPI('get_company_by_primary_director', userId, {});
 // Return null if no company found (not an error)
 if (!result.data) {
 return { success: true, data: null };
 }
 return { success: true, data: mapCompanyFromDB(result.data) };
 } catch (error: any) {
 console.error('Exception fetching company by primary director:', error);
 return { success: false, error: error.message || 'An unexpected error occurred' };
 }
 }

 /**
 * Update company account
 */
 static async updateCompanyAccount(
 companyId: string,
 userId: number,
 updates: UpdateCompanyAccountInput
 ): Promise<{ success: boolean; data?: CompanyAccount; error?: string }> {
 try {
 const result = await callCompanyAPI('update_company_account', userId, { companyId, updates });
 return { success: true, data: mapCompanyFromDB(result.data) };
 } catch (error: any) {
 console.error('Exception updating company:', error);
 return { success: false, error: error.message || 'An unexpected error occurred' };
 }
 }

 // ===========================================================================
 // OFFICER MANAGEMENT
 // ===========================================================================

 /**
 * Add an officer to a company
 */
 static async addOfficer(
 input: AddCompanyOfficerInput
 ): Promise<{ success: boolean; data?: CompanyOfficer; error?: string }> {
 try {
 const result = await callCompanyAPI('add_officer', input.userId, { input });
 return { success: true, data: mapOfficerFromDB(result.data) };
 } catch (error: any) {
 console.error('Exception adding officer:', error);
 return { success: false, error: error.message || 'An unexpected error occurred' };
 }
 }

 /**
 * Add a pending officer (not yet linked to a user account)
 * Used during company registration to pre-add officers who will be invited later
 */
 static async addPendingOfficer(input: {
 companyId: string;
 firstName: string;
 lastName: string;
 email: string;
 phone?: string;
 role: string;
 customRoleTitle?: string;
 shareholdingPercentage?: number;
 }): Promise<{ success: boolean; error?: string }> {
 try {
 await callCompanyAPI('add_pending_officer', null, { input });
 return { success: true };
 } catch (error: any) {
 console.error('Exception adding pending officer:', error);
 return { success: false, error: error.message || 'An unexpected error occurred' };
 }
 }

 /**
 * Get all officers for a company
 */
 static async getCompanyOfficers(
 companyId: string
 ): Promise<{ success: boolean; data?: CompanyOfficer[]; error?: string }> {
 try {
 const result = await callCompanyAPI('get_company_officers', null, { companyId });
 return { success: true, data: (result.data || []).map(mapOfficerFromDB) };
 } catch (error: any) {
 console.error('Exception fetching officers:', error);
 return { success: false, error: error.message || 'An unexpected error occurred' };
 }
 }

 /**
 * Update an existing officer's information
 */
 static async updateOfficer(
 officerId: string,
 updates: {
 role?: CompanyOfficerRole;
 customRoleTitle?: string;
 businessEmail?: string;
 businessPhone?: string;
 shareholdingPercentage?: number;
 hasSigningAuthority?: boolean;
 }
 ): Promise<{ success: boolean; error?: string }> {
 try {
 await callCompanyAPI('update_officer', null, { officerId, updates });
 return { success: true };
 } catch (error: any) {
 console.error('Exception updating officer:', error);
 return { success: false, error: error.message || 'An unexpected error occurred' };
 }
 }

 /**
 * Remove an officer from a company (soft delete - set is_current to false)
 */
 static async removeOfficer(
 officerId: string,
 resignationDate: string
 ): Promise<{ success: boolean; error?: string }> {
 try {
 await callCompanyAPI('remove_officer', null, { officerId, resignationDate });
 return { success: true };
 } catch (error: any) {
 console.error('Exception removing officer:', error);
 return { success: false, error: error.message || 'An unexpected error occurred' };
 }
 }

 // ===========================================================================
 // KYC STATUS MANAGEMENT
 // ===========================================================================

 /**
 * Submit company for KYC review
 */
 static async submitForKYC(
 companyId: string
 ): Promise<{ success: boolean; error?: string }> {
 try {
 await callCompanyAPI('submit_for_kyc', null, { companyId });
 return { success: true };
 } catch (error: any) {
 console.error('Exception submitting for KYC:', error);
 return { success: false, error: error.message || 'An unexpected error occurred' };
 }
 }

 /**
 * Update KYC status (admin function)
 */
 static async updateKYCStatus(
 companyId: string,
 status: CompanyKYCStatus,
 reviewedBy: string,
 rejectionReason?: string
 ): Promise<{ success: boolean; error?: string }> {
 try {
 await callCompanyAPI('update_kyc_status', null, { companyId, status, reviewedBy, rejectionReason });
 return { success: true };
 } catch (error: any) {
 console.error('Exception updating KYC status:', error);
 return { success: false, error: error.message || 'An unexpected error occurred' };
 }
 }

 // ===========================================================================
 // DOCUMENT & FULL COMPANY DATA
 // ===========================================================================

 /**
 * Get full company details with officers and documents
 */
 static async getCompanyFull(
 companyId: string
 ): Promise<{ success: boolean; data?: CompanyAccountFull; error?: string }> {
 try {
 const [companyResult, officersResult, documentsResult] = await Promise.all([
 this.getCompanyById(companyId),
 this.getCompanyOfficers(companyId),
 this.getCompanyDocuments(companyId)
 ]);

 if (!companyResult.success || !companyResult.data) {
 return { success: false, error: companyResult.error || 'Company not found' };
 }

 return {
 success: true,
 data: {
 ...companyResult.data,
 officers: officersResult.data || [],
 documents: documentsResult.data || []
 }
 };
 } catch (error) {
 console.error('Exception fetching full company:', error);
 return { success: false, error: 'An unexpected error occurred' };
 }
 }

 /**
 * Get all documents for a company
 */
 static async getCompanyDocuments(
 companyId: string
 ): Promise<{ success: boolean; data?: CompanyDocument[]; error?: string }> {
 try {
 const result = await callCompanyAPI('get_company_documents', null, { companyId });
 return { success: true, data: (result.data || []).map(mapDocumentFromDB) };
 } catch (error: any) {
 console.error('Exception fetching documents:', error);
 return { success: false, error: error.message || 'An unexpected error occurred' };
 }
 }
}

// Export singleton-style functions for easier use
export const createCompanyAccount = CompanyAccountService.createCompanyAccount.bind(CompanyAccountService);
export const getCompanyById = CompanyAccountService.getCompanyById.bind(CompanyAccountService);
export const getUserCompanies = CompanyAccountService.getUserCompanies.bind(CompanyAccountService);
export const updateCompanyAccount = CompanyAccountService.updateCompanyAccount.bind(CompanyAccountService);
export const addOfficer = CompanyAccountService.addOfficer.bind(CompanyAccountService);
export const addPendingOfficer = CompanyAccountService.addPendingOfficer.bind(CompanyAccountService);
export const getCompanyOfficers = CompanyAccountService.getCompanyOfficers.bind(CompanyAccountService);
export const updateOfficer = CompanyAccountService.updateOfficer.bind(CompanyAccountService);
export const removeOfficer = CompanyAccountService.removeOfficer.bind(CompanyAccountService);
export const submitForKYC = CompanyAccountService.submitForKYC.bind(CompanyAccountService);
export const updateKYCStatus = CompanyAccountService.updateKYCStatus.bind(CompanyAccountService);
export const getCompanyFull = CompanyAccountService.getCompanyFull.bind(CompanyAccountService);
export const getCompanyDocuments = CompanyAccountService.getCompanyDocuments.bind(CompanyAccountService);
